import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { isNativeEnv } from '../../utils/nativeBridge';
import {
  getHttpErrorStatus,
  isHttpUnauthenticatedError,
} from '../../utils/requests';
import {
  hasPassedIdleTimeThreshold,
  retrieveTokensUpdatedTimestamp,
  storeTokenUpdatedTimestamp,
  syncRefreshTokenWithInterval,
} from '../../utils/auth';
import {
  eventEmitter,
  IOS_TOKENS_REFRESH_FINISHED,
  IOS_TOKENS_REFRESH_INPROGRESS,
  TOKENS_UPDATED,
} from '../../utils/eventEmitter';
import {
  acApiHealthSetFetching,
  acApiHealthSetFetchingError,
  acSetApiHealth,
} from '../../store/actions/apiHealth';
import { LOCAL_API_HEALTH_STATUS } from '../../constants';
import { enableHealthCheck } from '../../config';
import { getApiHealthStatus } from '../../api/apiHealth';
import sessionStorage from '../../utils/sessionStorage';

// Milliseconds after app initialization during which reload should not happen
export const initializingThreshold = 10000;

const useSyncRefreshToken = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const setApiHealthStatus = (status) => dispatch(acSetApiHealth(status));
  const setApiHealthFetching = (fetching) =>
    dispatch(acApiHealthSetFetching(fetching));
  const setApiHealthError = (error) =>
    dispatch(acApiHealthSetFetchingError(error));

  const [initializedAt] = useState(Date.now());
  const [loading, setLoading] = useState(true);

  const shouldInitialize = (error) =>
    !isHttpUnauthenticatedError(getHttpErrorStatus(error));

  const onTokensUpdated = () => {
    if (
      isNativeEnv() &&
      Date.now() - initializedAt >= initializingThreshold &&
      hasPassedIdleTimeThreshold(retrieveTokensUpdatedTimestamp())
    ) {
      window.location = '/?refreshEnabled=true';
    }

    if (isNativeEnv()) {
      storeTokenUpdatedTimestamp();
    }
  };

  const onIosTokenRefresh = () => {
    setLoading(true);
  };

  const onIosTokenRecieved = () => {
    setLoading(false);
  };

  const emmiterFunctions = () => {
    eventEmitter.on(TOKENS_UPDATED, onTokensUpdated);
    eventEmitter.on(IOS_TOKENS_REFRESH_INPROGRESS, onIosTokenRefresh);
    eventEmitter.on(IOS_TOKENS_REFRESH_FINISHED, onIosTokenRecieved);
    return () => {
      eventEmitter.removeListener(
        IOS_TOKENS_REFRESH_INPROGRESS,
        onIosTokenRefresh
      );
      eventEmitter.removeListener(
        IOS_TOKENS_REFRESH_FINISHED,
        onIosTokenRecieved
      );
      eventEmitter.removeListener(TOKENS_UPDATED, onTokensUpdated);
    };
  };

  const callRefreshTokenEndpoint = async () => {
    // iOS shell handles tokens itself
    if (isNativeEnv()) {
      setLoading(false);
      return;
    }
    const [error] = await syncRefreshTokenWithInterval();

    if (shouldInitialize(error)) {
      setLoading(false);
    }
  };

  const setApiHealthStatusData = (data) => {
    setApiHealthStatus(data);
    sessionStorage.set(LOCAL_API_HEALTH_STATUS, data);
    if (data) {
      callRefreshTokenEndpoint();
      if (isNativeEnv() && !retrieveTokensUpdatedTimestamp()) {
        storeTokenUpdatedTimestamp();
      }
      return emmiterFunctions();
    }
    history.push('/status');
    return () => {};
  };

  const getApiStatus = async () => {
    setApiHealthFetching(true);
    setApiHealthError(null);
    if (enableHealthCheck === 'true') {
      if (!(sessionStorage.get(LOCAL_API_HEALTH_STATUS) === 'true')) {
        const [error, data] = await getApiHealthStatus();
        if (error) {
          setApiHealthError(error);
          setApiHealthStatusData(false);
        } else {
          setApiHealthStatusData(data);
        }
        setApiHealthFetching(false);
      } else {
        setApiHealthStatusData(true);
        setApiHealthFetching(false);
      }
    } else {
      setApiHealthStatusData(true);
    }
  };

  useEffect(() => {
    getApiStatus();
  }, []);

  return isNativeEnv() ? loading : false;
};

export default useSyncRefreshToken;
